import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Plus, FileText, Pencil, Trash2, Eye, Pin, Upload, Eraser } from "lucide-react";

const TYPES = [
  { value: "rental", label: "Rental" },
  { value: "birthday_party", label: "Birthday Party" },
  { value: "event", label: "Event" },
  { value: "picnic", label: "Picnic" },
  { value: "general", label: "General" },
  { value: "waiver", label: "Waiver" },
];

const MERGE_TAGS = [
  "{{client_name}}", "{{first_name}}", "{{last_name}}", "{{email}}", "{{phone}}",
  "{{address}}", "{{inquiry_type}}", "{{location}}", "{{event_date}}", "{{start_time}}",
  "{{duration}}", "{{venue_option}}", "{{num_guests}}", "{{child_age}}",
  "{{sport_activity}}", "{{num_players}}", "{{event_type}}", "{{expected_attendance}}",
  "{{catering_needs}}", "{{today_date}}", "{{deposit_amount}}", "{{total_amount}}",
  "{{payment_schedule}}", "{{custom_terms}}",
];

export default function ContractTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ name: "", type: "general", body: "", active: true });
  const [uploading, setUploading] = useState(false);
  const bodyRef = useRef(null);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/contract-templates")
      .then(({ data }) => setTemplates(data.templates))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", type: "general", body: "", active: true, brand: "any", fields: [] });
    setShowEditor(true);
  };

  const openEdit = (t) => {
    setEditing(t.id);
    setForm({ name: t.name, type: t.type, body: t.body, active: t.active, brand: t.brand || "any", fields: t.fields || [] });
    setShowEditor(true);
  };

  const slugify = (s) => (s || "").toString().toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

  const addField = () => setForm({ ...form, fields: [...(form.fields || []), { key: "", label: "", type: "text", default: "" }] });
  const updateField = (idx, patch) => {
    const fields = [...(form.fields || [])];
    fields[idx] = { ...fields[idx], ...patch };
    if (patch.label && !fields[idx].key) fields[idx].key = slugify(patch.label);
    setForm({ ...form, fields });
  };
  const removeField = (idx) => {
    const fields = [...(form.fields || [])];
    fields.splice(idx, 1);
    setForm({ ...form, fields });
  };

  const handleSave = async () => {
    if (!form.name || !form.body) { toast.error("Name and body are required"); return; }
    try {
      if (editing) {
        await api.put(`/contract-templates/${editing}`, form);
        toast.success("Template updated");
      } else {
        await api.post("/contract-templates", form);
        toast.success("Template created");
      }
      setShowEditor(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/contract-templates/${id}`);
      toast.success("Template deleted");
      load();
    } catch { toast.error("Failed to delete"); }
  };

  const insertTag = (tag) => {
    const ta = bodyRef.current;
    if (ta && typeof ta.selectionStart === "number") {
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const next = form.body.slice(0, start) + tag + form.body.slice(end);
      setForm({ ...form, body: next });
      // restore cursor after tag
      setTimeout(() => {
        ta.focus();
        const pos = start + tag.length;
        ta.setSelectionRange(pos, pos);
      }, 0);
    } else {
      setForm({ ...form, body: form.body + tag });
    }
  };

  const clearBody = () => {
    if (form.body && !window.confirm("Clear the contract body?")) return;
    setForm({ ...form, body: "" });
    setTimeout(() => bodyRef.current?.focus(), 0);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-upload of same file
    if (!file) return;
    const ok = !form.body || window.confirm("Replace current contract body with the uploaded document?");
    if (!ok) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/contract-templates/extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const baseName = file.name.replace(/\.[^.]+$/, "");
      setForm((f) => ({ ...f, body: data.text, name: f.name || baseName }));
      toast.success(`Imported ${file.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not import file");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="contract-templates-page">
      <div className="flex items-center justify-between">
        <h1 className="font-outfit text-2xl font-bold tracking-tight">Contract Templates</h1>
        <Button data-testid="create-template-btn" size="sm" onClick={openNew}>
          <Plus size={16} /> New Template
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No templates yet. Create your first contract template.
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map((t) => (
            <Card key={t.id} data-testid={`template-${t.id}`} className="border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <FileText size={18} className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{t.name}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className="text-xs capitalize">{t.type?.replace("_", " ")}</Badge>
                        <Badge variant="outline" className={`text-xs ${t.brand === "rrdc" ? "text-blue-400 border-blue-500/30" : t.brand === "riverview" ? "text-amber-400 border-amber-500/30" : "text-muted-foreground"}`}>
                          {t.brand === "rrdc" ? "Rolling River" : t.brand === "riverview" ? "Riverview" : "All"}
                        </Badge>
                        <Badge variant={t.active ? "default" : "secondary"} className="text-xs">{t.active ? "Active" : "Inactive"}</Badge>
                        {t.permanent_waiver && <Badge className="text-xs bg-green-500/20 text-green-500 border-green-500/30">Permanent Waiver</Badge>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {t.type === "waiver" && !t.permanent_waiver && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-green-500" title="Set as permanent waiver"
                        onClick={async () => { try { await api.put(`/contract-templates/${t.id}/set-permanent`); toast.success("Set as permanent waiver"); load(); } catch { toast.error("Failed"); } }}
                        data-testid={`set-permanent-${t.id}`}>
                        <Pin size={14} />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setPreview(t)} data-testid={`preview-${t.id}`}>
                      <Eye size={14} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(t)} data-testid={`edit-${t.id}`}>
                      <Pencil size={14} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDelete(t.id)} data-testid={`delete-${t.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Template Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Template" : "New Contract Template"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Template Name</label>
                <Input data-testid="template-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Turf House Rental Agreement" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Type</label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="template-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Assign To</label>
                <Select value={form.brand} onValueChange={(v) => setForm({ ...form, brand: v })}>
                  <SelectTrigger data-testid="template-brand"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">All / Either</SelectItem>
                    <SelectItem value="rrdc">Rolling River</SelectItem>
                    <SelectItem value="riverview">Riverview Athletics</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Merge Tags */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Insert Merge Tag</label>
              <div className="flex flex-wrap gap-1.5">
                {MERGE_TAGS.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => insertTag(tag)}
                    className="px-2 py-0.5 text-xs rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors font-mono"
                    data-testid={`tag-${tag}`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Fillable Fields */}
            <div className="space-y-2 rounded-lg border border-border p-3 bg-muted/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">Fillable Fields</p>
                  <p className="text-xs text-muted-foreground">Add custom fields you'll fill in when sending the contract. Use the tag below in the body.</p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addField} data-testid="add-field-btn">
                  <Plus size={14} /> Add Field
                </Button>
              </div>
              {(form.fields || []).length === 0 && (
                <p className="text-xs text-muted-foreground italic">No fillable fields yet. Add fields like "Hourly Rate", "Parking Available", or "Cash Payment".</p>
              )}
              {(form.fields || []).map((f, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start">
                  <Input
                    className="col-span-4"
                    data-testid={`field-label-${i}`}
                    placeholder="Label (e.g. Hourly Rate)"
                    value={f.label}
                    onChange={(e) => updateField(i, { label: e.target.value })}
                  />
                  <Select value={f.type} onValueChange={(v) => updateField(i, { type: v })}>
                    <SelectTrigger className="col-span-2" data-testid={`field-type-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">Text</SelectItem>
                      <SelectItem value="number">Number</SelectItem>
                      <SelectItem value="checkbox">Checkbox</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="col-span-5 flex items-center gap-2">
                    <code className="text-[11px] text-muted-foreground bg-background px-2 py-1.5 rounded border flex-1 truncate">{f.key ? `{{${f.key}}}` : "(label →)"}</code>
                    <Button
                      type="button" variant="ghost" size="sm"
                      onClick={() => f.key && insertTag(`{{${f.key}}}`)}
                      disabled={!f.key}
                      data-testid={`field-insert-${i}`}
                    >Insert</Button>
                  </div>
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeField(i)} className="col-span-1" data-testid={`field-remove-${i}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
              <p className="text-[11px] text-muted-foreground">Checkboxes render as <code className="bg-background px-1 rounded">[X]</code> when checked, <code className="bg-background px-1 rounded">[ ]</code> when unchecked.</p>
            </div>

            {/* Contract Body */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Contract Body</label>
                <div className="flex items-center gap-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".docx,.pdf,.txt,.md"
                    className="hidden"
                    onChange={handleUpload}
                    data-testid="template-upload-input"
                  />
                  <Button
                    type="button" variant="outline" size="sm"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid="template-upload-btn"
                  >
                    <Upload size={14} /> {uploading ? "Importing…" : "Upload .docx / .pdf"}
                  </Button>
                  <Button
                    type="button" variant="ghost" size="sm"
                    onClick={clearBody}
                    data-testid="template-clear-btn"
                  >
                    <Eraser size={14} /> Clear
                  </Button>
                </div>
              </div>
              <textarea
                ref={bodyRef}
                data-testid="template-body"
                className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring font-mono"
                rows={16}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder={"Click \"Upload\" above to import your existing contract or waiver, then click any merge tag to insert it at the cursor.\n\nExample:\n\nThis agreement is made on {{today_date}} between Rolling River and {{client_name}} for {{event_date}} at {{location}}."}
              />
              <p className="text-xs text-muted-foreground">Supported uploads: .docx, .pdf, .txt — text is extracted automatically. Click a merge tag above to insert it where the cursor is.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditor(false)}>Cancel</Button>
            <Button data-testid="save-template-btn" onClick={handleSave}>{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{preview?.name}</DialogTitle>
          </DialogHeader>
          <div className="bg-muted/30 rounded-lg p-6 font-mono text-sm whitespace-pre-wrap leading-relaxed">
            {preview?.body}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
